import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Mic, MicOff, Image as ImageIcon, Send, Target, Clock, GitBranch, Plus } from 'lucide-react';
import { neonQuery } from '../services/api';

// Tell Claude what you're actually trying to accomplish on one item - by typing,
// by talking, or by pasting a screenshot. Also carries the progress target (so one
// big job is a bar, not fifteen checkboxes) and an effort estimate for the
// capacity maths.

const imgUrl = (key) => `/.netlify/functions/r2-image?key=${encodeURIComponent(key)}`;

// Kept in step with the same list in Delegation.js and the HORIZONS check in neon.js.
const HORIZONS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'quarter', label: 'Next 3 months' },
  { key: 'someday', label: 'Someday' },
];

// Chrome ships webkitSpeechRecognition; everything else gets a hidden mic.
const SpeechRec = typeof window !== 'undefined'
  && (window.SpeechRecognition || window.webkitSpeechRecognition);

export default function WorkItemDetail({ item, onClose, onSaved }) {
  const [meta, setMeta] = useState({});
  const [notes, setNotes] = useState([]);
  const [children, setChildren] = useState([]);
  const [parent, setParent] = useState(null);
  const [owners, setOwners] = useState([]);
  const [piece, setPiece] = useState({ text: '', ownerKey: '', horizon: '', effortMinutes: '' });
  const [draft, setDraft] = useState('');
  const [shot, setShot] = useState(null);          // { dataUrl, filename, contentType }
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const recRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await neonQuery('work_item_detail', { srcTable: item.src_table, srcId: item.src_id });
      setMeta(d.meta || {});
      setNotes(d.notes || []);
      setChildren(d.children || []);
      setParent(d.parent || null);
    } catch (e) { setErr(e.message); }
  }, [item.src_table, item.src_id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let live = true;
    neonQuery('work_owners_list', {})
      .then((o) => { if (live) setOwners(o.owners || []); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const addPiece = async () => {
    if (!piece.text.trim()) return;
    setBusy(true);
    try {
      await neonQuery('work_add_child', {
        parentTable: item.src_table, parentId: item.src_id,
        text: piece.text, ownerKey: piece.ownerKey || null,
        horizon: piece.horizon || null, effortMinutes: piece.effortMinutes,
      });
      setPiece({ text: '', ownerKey: '', horizon: '', effortMinutes: '' });
      await load();
      onSaved && onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  // Stop the recogniser if the panel closes mid-sentence.
  useEffect(() => () => { try { recRef.current && recRef.current.stop(); } catch (_) {} }, []);

  const toggleMic = () => {
    if (!SpeechRec) return;
    if (listening) { try { recRef.current.stop(); } catch (_) {} setListening(false); return; }
    const rec = new SpeechRec();
    rec.continuous = true;
    rec.interimResults = false;
    rec.lang = 'en-US';
    rec.onresult = (e) => {
      let add = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) add += e.results[i][0].transcript;
      }
      if (add) setDraft((d) => (d ? `${d} ${add.trim()}` : add.trim()));
    };
    rec.onerror = (e) => { setErr(`Mic: ${e.error}`); setListening(false); };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    try { rec.start(); setListening(true); } catch (e) { setErr(e.message); }
  };

  const takeFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const fr = new FileReader();
    fr.onload = () => setShot({
      dataUrl: fr.result,
      filename: file.name || `screenshot-${Date.now()}.png`,
      contentType: file.type,
    });
    fr.readAsDataURL(file);
  };

  const onPaste = (e) => {
    const it = [...(e.clipboardData?.items || [])].find((x) => x.type.startsWith('image/'));
    if (it) { e.preventDefault(); takeFile(it.getAsFile()); }
  };

  const addNote = async () => {
    if (!draft.trim() && !shot) return;
    setBusy(true);
    try {
      let imageKey = null;
      if (shot) {
        const res = await fetch('/.netlify/functions/r2-upload', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: shot.filename,
            contentType: shot.contentType,
            data: shot.dataUrl.split(',')[1],
          }),
        });
        const j = await res.json();
        if (!res.ok || j.error) throw new Error(j.error || 'Screenshot upload failed');
        imageKey = j.key;
      }
      await neonQuery('work_note_add', {
        srcTable: item.src_table, srcId: item.src_id,
        body: draft.trim() || null, imageKey,
        source: listening ? 'voice' : 'typed',
      });
      setDraft(''); setShot(null);
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const saveMeta = async (patch) => {
    const next = { ...meta, ...patch };
    setMeta(next);
    setBusy(true);
    try {
      await neonQuery('work_meta_save', {
        srcTable: item.src_table, srcId: item.src_id,
        goal: next.goal, progressCurrent: next.progress_current,
        progressTarget: next.progress_target, progressUnit: next.progress_unit,
        effortMinutes: next.effort_minutes,
      });
      onSaved && onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const cur = Number(meta.progress_current);
  const tgt = Number(meta.progress_target);
  const hasBar = Number.isFinite(cur) && Number.isFinite(tgt) && cur !== tgt;
  // Countdowns run downward (75 -> 0), so measure distance travelled either way.
  const pct = hasBar
    ? Math.max(0, Math.min(100, Math.round(((Math.max(cur, tgt) - cur) / Math.abs(tgt - cur || 1)) * 100)))
    : 0;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,20,40,.45)', zIndex: 1000,
      display: 'flex', justifyContent: 'flex-end',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(560px, 100%)', height: '100%', background: '#fff', overflowY: 'auto',
        boxShadow: '-8px 0 28px rgba(0,0,0,.18)',
      }}>
        <div style={{
          position: 'sticky', top: 0, background: '#fff', borderBottom: '1px solid #eef1f4',
          padding: '16px 20px', display: 'flex', gap: 12, alignItems: 'flex-start', zIndex: 2,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 16 }}>{item.title}</div>
            <div style={{ fontSize: 11, color: '#8b94a3', marginTop: 3 }}>
              {item.domain}{item.owner_name ? ` · ${item.owner_name}` : ''}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b94a3' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '16px 20px' }}>
          {err && (
            <div style={{ background: '#fdecec', color: '#a12020', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 12 }}>
              {err}
            </div>
          )}

          <label className="form-label">What are you trying to accomplish?</label>
          <textarea className="form-textarea" rows={3} value={meta.goal || ''}
            placeholder="The context Claude needs to schedule this, delegate it, or do it for you."
            onChange={(e) => setMeta({ ...meta, goal: e.target.value })}
            onBlur={(e) => saveMeta({ goal: e.target.value })}
            style={{ width: '100%', marginBottom: 16 }} />

          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 6 }}>
            <div style={{ flex: '1 1 190px' }}>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Target size={12} /> Progress
              </label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input className="form-input" type="number" value={meta.progress_current ?? ''}
                  placeholder="from" onChange={(e) => setMeta({ ...meta, progress_current: e.target.value })}
                  onBlur={(e) => saveMeta({ progress_current: e.target.value })} style={{ width: 70 }} />
                <span style={{ color: '#8b94a3' }}>→</span>
                <input className="form-input" type="number" value={meta.progress_target ?? ''}
                  placeholder="to" onChange={(e) => setMeta({ ...meta, progress_target: e.target.value })}
                  onBlur={(e) => saveMeta({ progress_target: e.target.value })} style={{ width: 70 }} />
                <input className="form-input" value={meta.progress_unit || ''} placeholder="unit"
                  onChange={(e) => setMeta({ ...meta, progress_unit: e.target.value })}
                  onBlur={(e) => saveMeta({ progress_unit: e.target.value })} style={{ width: 78 }} />
              </div>
            </div>
            <div style={{ flex: '0 0 150px' }}>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <Clock size={12} /> Effort (minutes)
              </label>
              <input className="form-input" type="number" value={meta.effort_minutes ?? ''}
                placeholder="e.g. 90" onChange={(e) => setMeta({ ...meta, effort_minutes: e.target.value })}
                onBlur={(e) => saveMeta({ effort_minutes: e.target.value })} style={{ width: '100%' }} />
            </div>
          </div>

          {hasBar && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ height: 8, background: '#eef2f6', borderRadius: 20, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: 'var(--orange)' }} />
              </div>
              <div style={{ fontSize: 11, color: '#8b94a3', marginTop: 4 }}>
                {cur} {meta.progress_unit || ''} to go · target {tgt} · {pct}% of the way there
              </div>
            </div>
          )}

          {parent && (
            <div style={{ fontSize: 12, color: '#8b94a3', marginBottom: 12 }}>
              Piece of <strong style={{ color: 'var(--navy)' }}>{parent.title}</strong>
            </div>
          )}

          <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <GitBranch size={12} /> Pieces{children.length ? ` (${children.length})` : ''}
          </label>
          <div style={{ marginBottom: 8 }}>
            {children.map((c) => (
              <div key={`${c.src_table}-${c.src_id}`}
                style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '6px 0', borderBottom: '1px solid #f3f5f7' }}>
                <span style={{ flex: 1, fontSize: 13, color: '#2d3748' }}>{c.title}</span>
                <span style={{ fontSize: 11, color: c.owner_name ? 'var(--orange)' : '#b9c0cb', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {c.owner_name || 'unassigned'}
                </span>
                {c.horizon && <span style={{ fontSize: 11, color: '#8b94a3' }}>{c.horizon}</span>}
                {c.effort_minutes ? <span style={{ fontSize: 11, color: '#8b94a3' }}>{c.effort_minutes}m</span> : null}
              </div>
            ))}
            {!children.length && (
              <div style={{ fontSize: 12, color: '#b9c0cb', paddingBottom: 4 }}>
                Not broken down yet. Add a piece below and this becomes a project.
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
            <input className="form-input" placeholder="Another piece of this…" value={piece.text}
              onChange={(e) => setPiece({ ...piece, text: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') addPiece(); }}
              style={{ flex: '1 1 190px', fontSize: 13 }} />
            <select className="form-select" value={piece.ownerKey}
              onChange={(e) => setPiece({ ...piece, ownerKey: e.target.value })}
              style={{ maxWidth: 150, fontSize: 12 }}>
              <option value="">— who? —</option>
              {owners.map((o) => <option key={o.owner_key} value={o.owner_key}>{o.display_name}</option>)}
            </select>
            <select className="form-select" value={piece.horizon}
              onChange={(e) => setPiece({ ...piece, horizon: e.target.value })}
              style={{ maxWidth: 125, fontSize: 12 }}>
              <option value="">— when? —</option>
              {HORIZONS.map((h) => <option key={h.key} value={h.key}>{h.label}</option>)}
            </select>
            <input className="form-input" type="number" placeholder="mins" value={piece.effortMinutes}
              onChange={(e) => setPiece({ ...piece, effortMinutes: e.target.value })}
              style={{ width: 74, fontSize: 12 }} />
            <button className="btn btn-primary" onClick={addPiece} disabled={busy || !piece.text.trim()}
              style={{ padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Plus size={13} /> Add
            </button>
          </div>

          <label className="form-label">Notes, voice and screenshots</label>
          <div onPaste={onPaste}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); takeFile(e.dataTransfer.files[0]); }}
            style={{ border: '1px dashed #c9d1da', borderRadius: 8, padding: 10, marginBottom: 10 }}>
            <textarea className="form-textarea" rows={3} value={draft}
              placeholder="Type, talk, or paste a screenshot (Ctrl+V)…"
              onChange={(e) => setDraft(e.target.value)}
              style={{ width: '100%', border: 'none', resize: 'vertical', outline: 'none' }} />
            {shot && (
              <div style={{ position: 'relative', display: 'inline-block', marginTop: 8 }}>
                <img src={shot.dataUrl} alt="attachment" style={{ maxWidth: 200, borderRadius: 6, display: 'block' }} />
                <button onClick={() => setShot(null)} title="Remove"
                  style={{
                    position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,.6)', color: '#fff',
                    border: 'none', borderRadius: 20, cursor: 'pointer', width: 22, height: 22, lineHeight: 1,
                  }}>×</button>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
              {SpeechRec && (
                <button onClick={toggleMic} title={listening ? 'Stop' : 'Dictate'}
                  className={listening ? 'btn btn-danger' : 'btn btn-outline'}
                  style={{ padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 5 }}>
                  {listening ? <MicOff size={14} /> : <Mic size={14} />}
                  {listening ? 'Listening…' : 'Talk'}
                </button>
              )}
              <button className="btn btn-outline" onClick={() => fileRef.current.click()}
                style={{ padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 5 }}>
                <ImageIcon size={14} /> Screenshot
              </button>
              <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={(e) => takeFile(e.target.files[0])} />
              <button className="btn btn-primary" onClick={addNote} disabled={busy || (!draft.trim() && !shot)}
                style={{ marginLeft: 'auto', padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Send size={13} /> Add
              </button>
            </div>
          </div>
          {!SpeechRec && (
            <div style={{ fontSize: 11, color: '#b9c0cb', marginBottom: 10 }}>
              Dictation needs Chrome or Edge.
            </div>
          )}

          {notes.map((n) => (
            <div key={n.id} style={{ padding: '10px 0', borderBottom: '1px solid #f3f5f7' }}>
              {n.body && <div style={{ fontSize: 13, color: '#2d3748', lineHeight: 1.5 }}>{n.body}</div>}
              {n.image_key && (
                <a href={imgUrl(n.image_key)} target="_blank" rel="noreferrer">
                  <img src={imgUrl(n.image_key)} alt="note attachment"
                    style={{ maxWidth: '100%', borderRadius: 6, marginTop: 6, display: 'block' }} />
                </a>
              )}
              <div style={{ fontSize: 10, color: '#b9c0cb', marginTop: 5 }}>
                {n.created_at}{n.author ? ` · ${n.author}` : ''}{n.source === 'voice' ? ' · dictated' : ''}
              </div>
            </div>
          ))}
          {!notes.length && (
            <div style={{ fontSize: 12, color: '#b9c0cb' }}>No context yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
